// FlyerTemplatesAdmin — CON-TOOLS-02 v1 (Oct 27, 2026). Minimal template admin under Ops → Vecinos:
// turn a flyer archetype on/off and edit its 3 headlines (English on the card, Spanish gloss for the contractor). pricing_admin+ saves;
// every save is checked (3 headlines, 4–32 characters, never "Maestro" as the noun) and logged by ops_flyer_template_save.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { Chip, Notice, SectionLabel } from "./ui";

interface Tpl { key: string; version: number; active: boolean; flyer_id: string; headlines: Array<{ en: string; es: string }>; approved_by: string | null; approved_at: string | null }
const LABEL: Record<string, string> = { A_spotlight: "A · Neighbor spotlight", B_proof: "B · Before/after", C_prices: "C · Price grid", D_offer: "D · Offer" };

export default function FlyerTemplatesAdmin({ allowed }: { allowed: boolean }) {
  const [rows, setRows] = useState<Tpl[] | null>(null);
  const [edit, setEdit] = useState<Record<string, Tpl>>({});
  const [msg, setMsg] = useState<{ kind: "error" | "info"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc("ops_flyer_templates");
    if (error) setMsg({ kind: "error", text: error.message }); else setRows(data as Tpl[]);
  }, []);
  useEffect(() => { void load(); }, [load]);
  const k = (t: Tpl) => `${t.key}:${t.version}`;
  const save = async (t: Tpl) => {
    if (busy) return; setBusy(true); setMsg(null);
    const { error } = await supabase.rpc("ops_flyer_template_save", { p_key: t.key, p_version: t.version, p_active: t.active, p_headlines: t.headlines });
    setBusy(false);
    if (error) setMsg({ kind: "error", text: error.message });
    else { setMsg({ kind: "info", text: `Saved ${LABEL[t.key] ?? t.key}` }); setEdit((e) => { const n = { ...e }; delete n[k(t)]; return n; }); void load(); }
  };
  if (!rows) return null;
  return (
    <div className="card" style={{ marginTop: 24 }}>
      <SectionLabel>Flyer templates (what contractors can choose)</SectionLabel>
      {msg && (msg.kind === "error" ? <Notice kind="error">{msg.text}</Notice> : <p className="hint">✓ {msg.text}</p>)}
      {rows.map((r) => {
        const e = edit[k(r)];
        const t = e ?? r;
        return (
          <div key={k(r)} className="editor-section" style={{ borderTop: "1px solid #E3DFD7", paddingTop: 10, marginTop: 10 }}>
            <p style={{ margin: 0 }}><strong>{LABEL[r.key] ?? r.key}</strong> v{r.version} · {r.flyer_id} · <Chip tone={r.active ? "info" : "muted"}>{r.active ? "on" : "off"}</Chip>
              {r.approved_by ? <span className="hint"> · approved by {r.approved_by}</span> : null}</p>
            {!e && (
              <>
                <ol style={{ margin: "6px 0" }}>{r.headlines.map((h, i) => <li key={i}>{h.en} <span className="hint">— {h.es}</span></li>)}</ol>
                {allowed && <button type="button" className="btn quiet" onClick={() => setEdit((x) => ({ ...x, [k(r)]: structuredClone(r) }))}>Edit</button>}
              </>
            )}
            {e && (
              <>
                <label style={{ display: "block", margin: "6px 0" }}>
                  <input type="checkbox" checked={t.active} onChange={(ev) => setEdit((x) => ({ ...x, [k(r)]: { ...t, active: ev.target.checked } }))} /> Contractors can choose it
                </label>
                {t.headlines.map((h, i) => (
                  <div key={i} className="row-actions" style={{ marginTop: 6 }}>
                    <input value={h.en} maxLength={32} placeholder={`Headline ${i + 1} (English, ≤32)`} style={{ flex: 1 }}
                      onChange={(ev) => setEdit((x) => ({ ...x, [k(r)]: { ...t, headlines: t.headlines.map((y, j) => (j === i ? { ...y, en: ev.target.value.toUpperCase() } : y)) } }))} />
                    <input value={h.es} placeholder="Spanish gloss" style={{ flex: 1 }}
                      onChange={(ev) => setEdit((x) => ({ ...x, [k(r)]: { ...t, headlines: t.headlines.map((y, j) => (j === i ? { ...y, es: ev.target.value } : y)) } }))} />
                  </div>
                ))}
                <div className="row-actions" style={{ marginTop: 8 }}>
                  <button type="button" className="btn primary" disabled={busy} onClick={() => save(t)}>Save</button>
                  <button type="button" className="btn quiet" onClick={() => setEdit((x) => { const n = { ...x }; delete n[k(r)]; return n; })}>Cancel</button>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
